import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { db, auth, appId } from '../firebase-config';
import { collection, onSnapshot, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { PlusIcon } from './Icons';
import ProcessoDetalheModal from './ProcessoDetalheModal';
import InlineEdit from './InlineEdit';
import AddProcessoModal from './AddProcessoModal';

const ProcessoCard = ({ processo, index, onClick, onTitleUpdate }) => {
  return (
    <Draggable draggableId={processo.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`process-card rounded-xl p-4 mb-4 shadow-lg transition-all hover:shadow-xl hover:-translate-y-1 ${snapshot.isDragging ? 'ring-2 ring-primary-gold' : ''}`}
          onClick={() => onClick(processo)}
        >
          <div className="flex justify-between items-start mb-2">
            <InlineEdit
              value={processo.title}
              onSave={(newTitle) => onTitleUpdate(processo.id, newTitle)}
              className="font-semibold text-gray-900 flex-1"
            />
          </div>
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{processo.description || 'Sem descrição'}</p>
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Criado em: {processo.createdAt ? new Date(processo.createdAt.seconds * 1000).toLocaleDateString() : 'N/A'}</span>
          </div>
        </div>
      )}
    </Draggable>
  );
};

const Column = ({ title, processes, droppableId, onAddProcesso, onProcessoClick, onTitleUpdate }) => {
  return (
    <div className="glass-effect rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="heading-font text-xl font-bold text-white">{title}</h3>
        <div className="flex items-center space-x-2">
          <span className="bg-primary-navy text-white text-xs px-2 py-1 rounded-full">{processes.length}</span>
          <button onClick={() => onAddProcesso(droppableId)} className="text-primary-gold hover:text-accent-gold p-1" title="Adicionar Processo">
            <PlusIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
      <Droppable droppableId={droppableId}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`min-h-[60vh] rounded-lg p-2 transition-colors ${snapshot.isDraggingOver ? 'bg-white bg-opacity-10' : ''}`}
          >
            {processes.map((processo, index) => (
              <ProcessoCard key={processo.id} processo={processo} index={index} onClick={onProcessoClick} onTitleUpdate={onTitleUpdate} />
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
};

const Processos = () => {
  const [columns, setColumns] = useState({ todo: { title: 'A Fazer', processes: [] }, doing: { title: 'Em Andamento', processes: [] }, done: { title: 'Concluído', processes: [] }});
  const [selectedProcesso, setSelectedProcesso] = useState(null);
  const [isProcessoModalOpen, setIsProcessoModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [currentColumnId, setCurrentColumnId] = useState(null);
  const [loading, setLoading] = useState(true);
  const userId = auth.currentUser?.uid;

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    const unsubscribe = onSnapshot(collection(db, `artifacts/${appId}/users/${userId}/processos`), (snapshot) => {
        const processos = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const newColumns = { todo: { title: 'A Fazer', processes: [] }, doing: { title: 'Em Andamento', processes: [] }, done: { title: 'Concluído', processes: [] }};
        processos.forEach(processo => {
          const status = processo.status || 'todo';
          if (newColumns[status]) newColumns[status].processes.push(processo);
        });
        Object.keys(newColumns).forEach(key => newColumns[key].processes.sort((a,b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)));
        setColumns(newColumns);
        setLoading(false);
    }, (error) => {
      console.error("Erro ao carregar processos:", error);
      toast.error('Erro ao carregar processos');
      setLoading(false);
    });
    return () => unsubscribe();
  }, [userId, appId]);

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) return;

    try {
      await updateDoc(doc(db, `artifacts/${appId}/users/${userId}/processos`, draggableId), { status: destination.droppableId, updatedAt: serverTimestamp() });
      toast.success('Processo movido com sucesso!');
    } catch (error) {
      toast.error('Erro ao mover processo');
    }
  };
  
  const openAddProcessoModal = (columnId) => {
    setCurrentColumnId(columnId);
    setIsAddModalOpen(true);
  };

  const handleAddProcesso = async (columnId, processoData) => {
    if (!userId) return;
    try {
      await addDoc(collection(db, `artifacts/${appId}/users/${userId}/processos`), {
        ...processoData,
        status: columnId,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      toast.success('Processo criado com sucesso!');
    } catch (error) {
      toast.error('Erro ao criar processo');
    }
  };
  
  const handleProcessoClick = (processo) => {
    setSelectedProcesso(processo);
    setIsProcessoModalOpen(true);
  };

  const handleTitleUpdate = async (processoId, newTitle) => {
    try {
      await updateDoc(doc(db, `artifacts/${appId}/users/${userId}/processos`, processoId), { title: newTitle, updatedAt: serverTimestamp() });
      toast.success('Título atualizado com sucesso!');
    } catch (error) {
      toast.error('Erro ao atualizar título');
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-full"><div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-gold"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="heading-font text-3xl font-bold text-white">Processos</h1>
          <p className="mt-1 text-gray-300">Gerencie seus processos com a metodologia Kanban.</p>
        </div>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.entries(columns).map(([columnId, column]) => (
            <Column key={columnId} title={column.title} processes={column.processes} droppableId={columnId} onAddProcesso={openAddProcessoModal} onProcessoClick={handleProcessoClick} onTitleUpdate={handleTitleUpdate} />
          ))}
        </div>
      </DragDropContext>
      
      <AddProcessoModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSave={handleAddProcesso} columnId={currentColumnId} />
      {selectedProcesso && <ProcessoDetalheModal processoId={selectedProcesso.id} isOpen={isProcessoModalOpen} onClose={() => setIsProcessoModalOpen(false)} />}
    </div>
  );
};

export default Processos;
